import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Cards, Container, Header, SelectProps, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import ServiceCollection from 'src/services/ServiceCollection';
import TPENavigator from 'src/components/TPENavigator';
import { ACTION_TYPE, TPEBasicModal } from 'src/components/shared/TPEBasicModal';
import { getPermissions } from 'src/components/AppPermissions';
import { AppModules } from 'src/models/permissions/RolePermissions';
import { ReportRunType } from 'src/models/reports/ReportVersions';
import { REPORT_DETAILS } from '../ReportDefinitionConstants';
import { TP_TAX_REPORT_DETAILS } from '../DashboardConstants';
import { getHeaderActionDetailsForTPTaxReportCard } from '../CardHeaderDefinitions';
import ReportFilterBar from './ReportFilterBar';
import ReportContextBanner from './ReportContextBanner';
import { FULL_REFRESH_MESSAGES, POST_TRIGGER_VERSIONS_REFRESH_DELAY_MS } from './tpTaxSectionConstants';
import { toOrchestratorPeriod, deriveAccountingDay } from 'src/models/reports/ReportPeriod';

type ComponentProps = {
    services: ServiceCollection;
};

// Report types that a VAR + PLI refresh does NOT regenerate — disabled when such a version is selected.
const VAR_PLI_EXCLUDED_REPORTS = new Set([
    REPORT_DETAILS.TP_TAX,
    REPORT_DETAILS.TP_TAX_CWB_REPORT,
    REPORT_DETAILS.TP_TAX_LEF_REPORT,
]);

/**
 * Redesigned "Transfer Pricing Reports" section (Product mockup New_mockup_reporting.html), rendered
 * in place of the legacy card grid when the eventDrivenTpTaxReportingEnabled feature flag is on. Adds an
 * accounting-period + report-version selector, contextual banners, and card enable/disable driven by
 * the selected version's run type. In-progress runs are surfaced as disabled "in progress" entries in
 * the version selector (there is no separate job-status bar). The version + status data comes from the
 * orchestrator GetTPReportVersions op (via ReportsService.getReportVersions).
 */
export default function TPTaxReportsSection(props: ComponentProps) {
    const [navigateURL, setNavigateURL] = useState('');
    const [selectedPeriod, setSelectedPeriod] = useState('');
    const [selectedVersionId, setSelectedVersionId] = useState<string | undefined>(undefined);

    // Full Refresh: confirmation modal visibility, the period being triggered (null while idle — drives
    // the trigger hook), and a bump key that forces the version list to re-fetch after a successful run.
    const [showFullRefreshModal, setShowFullRefreshModal] = useState(false);
    const [fullRefreshPeriod, setFullRefreshPeriod] = useState<string | null>(null);
    const [versionsRefreshKey, setVersionsRefreshKey] = useState(0);
    // True from the moment a trigger is accepted until the delayed version re-fetch completes. Drives an
    // inline "updating" loader + keeps the controls disabled so the list doesn't appear to change out of
    // nowhere after the delay — the user sees a continuous "working" state instead of a sudden jump.
    const [refreshingAfterTrigger, setRefreshingAfterTrigger] = useState(false);

    const { canTriggerOnDemandReporting } = getPermissions(AppModules.REPORTING);

    const [periodsResponse, periodsLoading, periodsError] = props.services.reportsService.getReportingPeriods();
    const [versionsResult, versionsLoading, versionsError] =
        props.services.reportsService.getReportVersions(selectedPeriod, versionsRefreshKey);
    const [fullRefreshResult, fullRefreshLoading, fullRefreshError] =
        props.services.reportsService.triggerOnDemandFullRefresh(fullRefreshPeriod);

    const periodOptions: SelectProps.Option[] = useMemo(
        () => (periodsResponse?.ReportingPeriodList ?? []).map((p) => ({ label: p, value: p })),
        [periodsResponse],
    );

    useEffect(() => {
        if (periodsError) {
            props.services.messageService.showErrorAutoDismissBanner(periodsError);
        }
    }, [periodsError]);

    useEffect(() => {
        if (versionsError) {
            props.services.messageService.showErrorAutoDismissBanner(versionsError);
        }
    }, [versionsError]);

    // Full Refresh succeeded: show a success banner (driven off the actual result, not the click) and
    // re-arm the trigger hook immediately. The version-list refresh is DELAYED: the run is created
    // asynchronously downstream of the trigger (SQS → coordinator → the lambda that writes the
    // PHASE1_RUNNING row), so refetching right away would race that pipeline and show the stale list
    // without the new run. The timer is cleared on unmount / re-trigger so a late refetch can't fire
    // against a stale closure.
    useEffect(() => {
        if (!fullRefreshResult) {
            return undefined;
        }
        props.services.messageService.showSuccessAutoDismissBanner(FULL_REFRESH_MESSAGES.SUCCESS);
        setFullRefreshPeriod(null);
        // Enter the "settling" state now (loader shows immediately, right after the banner) and hold it
        // through the delayed re-fetch; it is cleared when that re-fetch's loading settles (see below).
        setRefreshingAfterTrigger(true);
        const timer = setTimeout(
            () => setVersionsRefreshKey((key) => key + 1),
            POST_TRIGGER_VERSIONS_REFRESH_DELAY_MS,
        );
        return () => clearTimeout(timer);
    }, [fullRefreshResult]);

    // Clear the settling state once the delayed re-fetch actually completes — detected on the falling
    // edge of versionsLoading (true → false). This ties the loader to the real fetch finishing rather
    // than a second guessed timer, so the loader ends exactly when the fresh list is rendered.
    const prevVersionsLoading = useRef(versionsLoading);
    useEffect(() => {
        if (refreshingAfterTrigger && prevVersionsLoading.current && !versionsLoading) {
            setRefreshingAfterTrigger(false);
        }
        prevVersionsLoading.current = versionsLoading;
    }, [versionsLoading, refreshingAfterTrigger]);

    // Full Refresh failed (e.g. 409 a job is already active for the period): show the error and re-arm
    // the trigger hook so a corrected retry re-fires.
    useEffect(() => {
        if (fullRefreshError) {
            props.services.messageService.showErrorBanner(fullRefreshError);
            setFullRefreshPeriod(null);
        }
    }, [fullRefreshError]);

    // Reset the selected version the moment the period changes, so the dropdown and cards revert to
    // their initial (nothing-selected) state instead of briefly showing the previous period's selection
    // while the new period's versions load.
    useEffect(() => {
        setSelectedVersionId(undefined);
    }, [selectedPeriod]);

    // When the versions for a period arrive, default the selection to the newest selectable (non
    // in-progress) version so the cards reflect the latest viewable report.
    useEffect(() => {
        const firstViewable = versionsResult.versions.find((v) => !v.inProgress);
        setSelectedVersionId(firstViewable?.jobId);
    }, [versionsResult]);

    const selectedVersion = useMemo(
        () => versionsResult.versions.find((v) => v.jobId === selectedVersionId),
        [versionsResult, selectedVersionId],
    );

    // Query string handed to the report pages so they inherit the dashboard's period + accounting-day
    // selection instead of showing their own period/day dropdowns. The accounting day is derived from the
    // selected version's trigger type + period (the version DTO carries no structured day). Empty until a
    // viewable version is selected — but the cards are disabled in that state, so navigation can't fire
    // without it.
    const reportNavQuery = useMemo(() => {
        if (!selectedPeriod || !selectedVersion) {
            return '';
        }
        const accountingDay = deriveAccountingDay(selectedVersion.triggerType, selectedPeriod);
        return `?period=${encodeURIComponent(selectedPeriod)}&accountingDay=${encodeURIComponent(accountingDay)}`;
    }, [selectedPeriod, selectedVersion]);

    // Full Refresh is disabled without a selected period, while a trigger is in flight, or while a run
    // is already in progress for the period (the backend would reject a concurrent trigger with a 409).
    // Also disabled WHILE the version list is loading: until it resolves we don't yet know whether a
    // run is in progress, so enabling the button here would let the user trigger into a race (a running
    // report may be about to appear in the results).
    const aRunIsInProgress = versionsResult.versions.some((v) => v.inProgress);
    const fullRefreshDisabled =
        !selectedPeriod || versionsLoading || fullRefreshLoading || refreshingAfterTrigger || aRunIsInProgress;

    const isCardDisabled = (reportDetails: { headerName: string; reportType: string }): boolean => {
        // No viewable version -> everything is unavailable.
        if (!selectedVersion) {
            return true;
        }
        // A VAR + PLI refresh only produces VAR (and the PLI section of MAR); MAR/CWB/LEF are stale.
        if (selectedVersion.runType === ReportRunType.VAR_PLI) {
            return VAR_PLI_EXCLUDED_REPORTS.has(reportDetails);
        }
        return false;
    };

    return (
        <>
            <TPENavigator path={navigateURL} />
            <Container
                header={
                    <Header
                        variant="h2"
                        actions={<Button onClick={() => setNavigateURL('/reports/history')}>History</Button>}
                    >
                        Transfer Pricing Reports
                    </Header>
                }
            >
                <SpaceBetween size="m">
                    <ReportFilterBar
                        periodOptions={periodOptions}
                        periodsLoading={periodsLoading}
                        selectedPeriod={selectedPeriod}
                        onPeriodChange={setSelectedPeriod}
                        versions={versionsResult.versions}
                        selectedVersionId={selectedVersionId}
                        onVersionChange={setSelectedVersionId}
                        versionsLoading={versionsLoading}
                        canGenerate={canTriggerOnDemandReporting}
                        onFullRefresh={() => setShowFullRefreshModal(true)}
                        fullRefreshDisabled={fullRefreshDisabled}
                    />

                    {refreshingAfterTrigger && (
                        <Box data-testid="post-trigger-refresh-loader" color="text-status-info">
                            <SpaceBetween size="xs" direction="horizontal">
                                <Spinner />
                                <span>{FULL_REFRESH_MESSAGES.REFRESHING}</span>
                            </SpaceBetween>
                        </Box>
                    )}

                    <ReportContextBanner
                        periodSelected={!!selectedPeriod}
                        loading={versionsLoading}
                        versions={versionsResult.versions}
                        selectedVersion={selectedVersion}
                        isQuarterEndPeriod={versionsResult.isQuarterEndPeriod}
                    />

                    <Cards
                        cardDefinition={{
                            header: (item) =>
                                getHeaderActionDetailsForTPTaxReportCard(
                                    item.name,
                                    () => undefined,
                                    setNavigateURL,
                                    () => undefined,
                                    isCardDisabled(item.name),
                                    reportNavQuery,
                                ),
                            sections: [
                                {
                                    id: 'description',
                                    header: 'Description',
                                    content: (item) => item.description,
                                },
                            ],
                        }}
                        cardsPerRow={[{ cards: 3 }]}
                        items={TP_TAX_REPORT_DETAILS}
                    />
                </SpaceBetween>
            </Container>

            <TPEBasicModal
                title={FULL_REFRESH_MESSAGES.CONFIRM_TITLE}
                action={ACTION_TYPE.CONFIRM_CANCEL}
                visible={showFullRefreshModal}
                onConfirm={() => {
                    setShowFullRefreshModal(false);
                    // Convert to the yyyyMM the orchestrator expects and fire the trigger hook. The
                    // success/error banners are driven off the settled result, not this click.
                    setFullRefreshPeriod(toOrchestratorPeriod(selectedPeriod));
                }}
                onCancel={() => setShowFullRefreshModal(false)}
            >
                <Box>{FULL_REFRESH_MESSAGES.confirmBody(selectedPeriod)}</Box>
            </TPEBasicModal>
        </>
    );
}
